import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt, faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { faApple, faGooglePlay } from '@fortawesome/free-brands-svg-icons';
import { useLanguage } from '../contexts/LanguageContext';

import allPOIs from '../resources/pois.json';

function fetchPoiById(id) {
  return allPOIs.find((p) => String(p.id) === String(id)) || null;
}

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #14464F 0%, #3C8178 100%);
  display: flex;
  flex-direction: column;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 20px 40px;
  min-height: calc(100svh - 88px);
`;

const Card = styled.div`
  background: white;
  border-radius: 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  max-width: 480px;
  width: 100%;
  overflow: hidden;
  animation: slideUp 0.5s ease-out;

  @keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;

const POIImageContainer = styled.div`
  width: 100%;
  height: 240px;
  background: linear-gradient(135deg, #14464F, #3C8178);
  position: relative;
  overflow: hidden;
`;

const POIImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const POIImagePlaceholder = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255,255,255,0.5);
  font-size: 3rem;
`;

const CardBody = styled.div`
  padding: 32px 28px;
  text-align: center;
`;

const POIName = styled.h1`
  font-size: 1.6rem;
  font-weight: 700;
  color: #14464F;
  margin: 0 0 8px 0;
`;

const POIType = styled.p`
  font-size: 0.95rem;
  color: #888;
  margin: 0 0 20px 0;
  text-transform: capitalize;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #eee;
  margin: 0 0 24px 0;
`;

const Message = styled.p`
  font-size: 1rem;
  color: #555;
  line-height: 1.5;
  margin: 0 0 28px 0;
`;

const OpenAppButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  background: linear-gradient(135deg, #14464F, #3C8178);
  color: white;
  padding: 14px 28px;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  margin-bottom: 16px;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(20, 70, 79, 0.3);
    color: white;
    text-decoration: none;
  }
`;

const StoreLinks = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 8px;
`;

const StoreButton = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s ease;
  border: 2px solid #14464F;
  color: #14464F;
  background: white;

  &:hover {
    background: #14464F;
    color: white;
    text-decoration: none;
    transform: translateY(-1px);
  }
`;

const SubText = styled.p`
  font-size: 0.8rem;
  color: #aaa;
  margin: 16px 0 0 0;
`;

const LoadingCard = styled(Card)`
  padding: 60px 28px;
  text-align: center;
`;

const Spinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid #eee;
  border-top-color: #14464F;
  border-radius: 50%;
  margin: 0 auto 16px;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const IOS_STORE_URL = 'https://apple.co/44ajr0e';
const ANDROID_STORE_URL = 'https://bit.ly/QuisqueyaQuest';

/**
 * Extract numeric ID from a slug like "playa-rincon-1" → "1"
 */
function extractIdFromSlug(slug) {
  const match = slug.match(/-(\d+)$/);
  return match ? match[1] : null;
}

const POIPage = () => {
  const { id: rawId, slug } = useParams();
  const poiId = slug ? extractIdFromSlug(slug) : rawId;
  const { language } = useLanguage();
  const [poi, setPoi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [poiImage, setPoiImage] = useState(null);
  const [fetchError, setFetchError] = useState(null);

  useEffect(() => {
    const fetchPOI = async () => {
      try {
        const data = fetchPoiById(poiId);
        if (!data) throw new Error('POI not found');
        setPoi(data);

        if (data?.image_url) {
          if (typeof data.image_url === 'string' && data.image_url.startsWith('[')) {
            try {
              const imageArray = JSON.parse(data.image_url);
              if (Array.isArray(imageArray) && imageArray.length > 0) setPoiImage(imageArray[0]);
            } catch {
              setPoiImage(data.image_url);
            }
          } else if (typeof data.image_url === 'string' && data.image_url.length > 0) {
            setPoiImage(data.image_url);
          }
        }
      } catch (err) {
        setFetchError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (poiId) {
      fetchPOI();
    } else {
      setLoading(false);
    }
  }, [poiId]);

  // Try to open the app on mobile browsers
  useEffect(() => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile && poiId) {
      // Use the slug-based deeplink if available, otherwise fall back to id-based
      const appUrl = slug
        ? `quisqueyaquest://explore/${slug}`
        : `quisqueyaquest://poi/${poiId}`;
      const timeout = setTimeout(() => {
        // App didn't open — user stays on this page
      }, 2500);

      window.location.href = appUrl;

      // If the page becomes hidden, the app opened
      const handleVisibility = () => {
        if (document.hidden) {
          clearTimeout(timeout);
        }
      };
      document.addEventListener('visibilitychange', handleVisibility);
      return () => {
        clearTimeout(timeout);
        document.removeEventListener('visibilitychange', handleVisibility);
      };
    }
  }, [poiId, slug]);

  const poiName = poi
    ? (language === 'es' && poi.name_es ? poi.name_es : poi.name)
    : (language === 'es' ? 'Punto de Interés' : 'Point of Interest');

  if (loading) {
    return (
      <PageContainer>
        <Navbar minimal />
        <ContentWrapper>
          <LoadingCard>
            <Spinner />
            <Message>{language === 'es' ? 'Cargando...' : 'Loading...'}</Message>
          </LoadingCard>
        </ContentWrapper>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar minimal />
      <ContentWrapper>
        <Card>
          <POIImageContainer>
            {poiImage ? (
              <POIImg src={poiImage} alt={poiName} />
            ) : (
              <POIImagePlaceholder>
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </POIImagePlaceholder>
            )}
          </POIImageContainer>

          <CardBody>
            <POIName>{poiName}</POIName>
            <Divider />
            <Message>
              {language === 'es'
                ? 'Abre este lugar en Quisqueya Quest para ver detalles, fotos, rutas y mucho más.'
                : 'Open this place in Quisqueya Quest to see details, photos, directions, and more.'}
            </Message>

            <OpenAppButton href={slug ? `quisqueyaquest://explore/${slug}` : `quisqueyaquest://poi/${poiId}`}>
              <FontAwesomeIcon icon={faExternalLinkAlt} />
              {language === 'es' ? 'Abrir en la App' : 'Open in App'}
            </OpenAppButton>

            <StoreLinks>
              <StoreButton href={IOS_STORE_URL} target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faApple} />
                App Store
              </StoreButton>
              <StoreButton href={ANDROID_STORE_URL} target="_blank" rel="noopener noreferrer">
                <FontAwesomeIcon icon={faGooglePlay} />
                Google Play
              </StoreButton>
            </StoreLinks>

            <SubText>
              {language === 'es'
                ? 'Descarga gratis Quisqueya Quest'
                : 'Download Quisqueya Quest for free'}
            </SubText>
          </CardBody>
        </Card>
      </ContentWrapper>
      <Footer />
    </PageContainer>
  );
};

export default POIPage;
